import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, History, AlertTriangle, ChevronLeft, Link2, ShieldCheck, Gem, Calculator, Info, Wand2, Unlink } from 'lucide-react';

const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;

interface Cat { category_id: string; name: string; weight: number; rationale?: string }
interface Factor { factor_id: string; category_id: string; survey_factor: string; survey_ref: string; scoring_type: string; weight_in_category: number; max_points: number; question_code?: string }
interface Lookup { factor_id: string; possible_answer: string | null; band_from: unknown; risk_points: unknown }
interface RiskBand { band: string; lower: number; upper: number; description?: string; stance?: string }
interface AttnRule { rule_id: string; attention_level: string; description?: string; metric_1?: string; threshold_1?: number; metric_2?: string; threshold_2?: number; active?: string }
interface MapQuestion { question_code: string; display_number?: string; text?: string; section?: string; type?: string }
interface MapFactor { factor_id: string; suggested_question_code?: string | null }
interface MappingResp { matrix_id: string; template_id: string | null; linked_templates: Array<{ id: string; name: string }>; questions: MapQuestion[]; factors: MapFactor[]; mapped_count: number; factor_count: number; resolution?: { sample_result_id: string; scored_ok: number; missing: number; unresolved: number; not_applicable: number; sample_score: number } | null }
export interface JbConfig {
  name?: string; settings?: Record<string, unknown>;
  categories: Cat[]; factors: Factor[]; scoring_lookup: Lookup[];
  risk_bands: RiskBand[]; attention_rules: AttnRule[];
}
interface VersionRow { id: string; version_number: number; engine: string; is_current: boolean; change_summary?: string; created_at: string; created_by_email: string | null }
interface MatrixDetailJB { id: string; name: string; description: string; engine: string; version_number: number; config: JbConfig; linked_templates: Array<{ id: string; name: string }> }

const CAT_LABELS: Record<string, string> = { EXP: 'Exposure', STK: 'Stock', HRS: 'Trading Hours', LOC: 'Location', EXT: 'External Security', INT: 'Internal Security', DSP: 'Display', ALM: 'Alarm', CCTV: 'CCTV', SAFE: 'Safes', STM: 'Stock Management', EMP: 'Employees', FIRE: 'Fire & Resilience' };
const catName = (c: { category_id: string; name?: string }): string => CAT_LABELS[c.category_id] || c.name || c.category_id;
const bandColor = (b?: string): string => ({ 'Very Low': 'bg-emerald-500', Low: 'bg-green-500', Moderate: 'bg-yellow-500', High: 'bg-orange-500', 'Very High': 'bg-red-600' }[b || ''] || 'bg-slate-400');
const scoreColor = (s: number | null): string => s == null ? 'bg-slate-300' : s < 15 ? 'bg-emerald-500' : s < 25 ? 'bg-green-500' : s < 40 ? 'bg-yellow-500' : s < 55 ? 'bg-orange-500' : 'bg-red-600';
const attnVariant = (a?: string): 'destructive' | 'warning' | 'muted' => ({ Critical: 'destructive', High: 'warning', Medium: 'warning', Low: 'muted' } as Record<string, 'destructive' | 'warning' | 'muted'>)[a || ''] || 'muted';

const mapBands = (rb: RiskBand[]) => rb.map((b) => ({ band: b.band, upper_exclusive: Number(b.upper) >= 100 ? null : Number(b.upper), label: b.band }));

export const JbMatrixEditor: React.FC<{ matrix: MatrixDetailJB; versions: VersionRow[]; onReload: () => void }> = ({ matrix, versions, onReload }) => {
  const nav = useNavigate();
  const [config, setConfig] = useState<JbConfig>(() => matrix.config);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [calc, setCalc] = useState<Record<string, unknown> | null>(null);
  const [calculating, setCalculating] = useState(false);

  const patch = (p: Partial<JbConfig>) => { setConfig((c) => ({ ...c, ...p })); setDirty(true); };

  const weightSum = useMemo(() => config.categories.reduce((s, c) => s + Number(c.weight || 0), 0), [config.categories]);
  const weightOk = Math.abs(weightSum - 1) < 0.001;

  const lookupByFactor = useMemo(() => {
    const m: Record<string, Lookup[]> = {};
    (config.scoring_lookup || []).forEach((l) => { (m[l.factor_id] ||= []).push(l); });
    return m;
  }, [config.scoring_lookup]);
  const lookupRowsByFactor = useMemo(() => {
    const m: Record<string, Array<{ l: Lookup; i: number }>> = {};
    (config.scoring_lookup || []).forEach((l, i) => { (m[l.factor_id] ||= []).push({ l, i }); });
    return m;
  }, [config.scoring_lookup]);
  const factorsByCat = useMemo(() => {
    const m: Record<string, Factor[]> = {};
    (config.factors || []).forEach((f) => { (m[f.category_id] ||= []).push(f); });
    return m;
  }, [config.factors]);
  const isDropdown = (f: Factor): boolean => ['Dropdown answer', 'Weakest of safes 1-3'].includes(f.scoring_type);

  const publish = async () => {
    if (!changeSummary.trim()) { toast.error('Describe what changed — it is stamped on the version history.'); return; }
    if (!weightOk) { toast.error('Category weights must sum to 1.00 before publishing.'); return; }
    setSaving(true);
    try {
      await api.post(`/risk/matrices/${matrix.id}/versions`, {
        engine: 'jb_v1', bands: mapBands(config.risk_bands), config, change_summary: changeSummary,
      });
      toast.success(`New version published — existing assessments stay on v${matrix.version_number}`);
      setDirty(false); setChangeSummary(''); onReload();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Publish failed');
    } finally { setSaving(false); }
  };

  const runCalc = async () => {
    setCalculating(true);
    try {
      const r = await api.post(`/risk/matrices/${matrix.id}/jb-evaluate`, { answers });
      setCalc(r.data as Record<string, unknown>);
    } catch { toast.error('Failed to compute rating.'); } finally { setCalculating(false); }
  };

  const setCat = (i: number, weight: number) => { const categories = [...config.categories]; categories[i] = { ...categories[i], weight }; patch({ categories }); };
  const setFactor = (fid: string, key: 'weight_in_category' | 'max_points', v: number) => {
    const factors = config.factors.map((f) => f.factor_id === fid ? { ...f, [key]: v } : f); patch({ factors });
  };
  const setBand = (i: number, key: keyof RiskBand, v: string | number) => { const risk_bands = [...config.risk_bands]; risk_bands[i] = { ...risk_bands[i], [key]: v }; patch({ risk_bands }); };
  const setLookup = (i: number, key: 'possible_answer' | 'band_from' | 'risk_points' | 'rationale', v: string | number) => {
    const scoring_lookup = config.scoring_lookup.map((l, idx) => idx === i ? { ...l, [key]: v } : l); patch({ scoring_lookup });
  };

  // ---- Survey-question mapping ----
  const [mapData, setMapData] = useState<MappingResp | null>(null);
  const [mapTemplateId, setMapTemplateId] = useState<string>('');
  const [mapLoading, setMapLoading] = useState(false);

  const loadMapping = async (tid?: string): Promise<void> => {
    setMapLoading(true);
    try {
      const q = (tid ?? mapTemplateId) ? `?template_id=${tid ?? mapTemplateId}` : '';
      const r = await api.get<MappingResp>(`/risk/matrices/${matrix.id}/survey-mapping${q}`);
      setMapData(r.data);
      setMapTemplateId(r.data.template_id || '');
    } catch { toast.error('Failed to load survey questions'); } finally { setMapLoading(false); }
  };
  useEffect(() => { void loadMapping(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const setFactorQuestion = (fid: string, qc: string): void => {
    const factors = config.factors.map((f) => f.factor_id === fid ? { ...f, question_code: qc || undefined } : f);
    patch({ factors });
  };
  const autoMapAll = (): void => {
    if (!mapData) return;
    const sugg: Record<string, string> = {};
    mapData.factors.forEach((f) => { if (f.suggested_question_code) sugg[f.factor_id] = f.suggested_question_code; });
    const factors = config.factors.map((f) => sugg[f.factor_id] ? { ...f, question_code: sugg[f.factor_id] } : f);
    patch({ factors });
    toast.success(`Auto-mapped ${Object.keys(sugg).length} factor(s) — review and publish to apply`);
  };
  const suggestionFor = (fid: string): string | undefined =>
    mapData?.factors.find((f) => f.factor_id === fid)?.suggested_question_code || undefined;
  const mappedCount = useMemo(() => (config.factors || []).filter((f) => f.question_code).length, [config.factors]);
  const setRule = (i: number, key: keyof AttnRule, v: string | number) => { const attention_rules = [...config.attention_rules]; attention_rules[i] = { ...attention_rules[i], [key]: v }; patch({ attention_rules }); };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="jb-matrix-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <button className="hover:underline flex items-center gap-1 text-raptor-navy/60 text-xs" onClick={() => nav('..')} data-testid="back-to-matrices">
            <ChevronLeft size={12} /> Risk matrices
          </button>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide mt-1 truncate flex items-center gap-2"><Gem size={20} className="text-raptor-blue" /> {matrix.name}</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">{matrix.description}</p>
          {matrix.linked_templates.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-raptor-navy/60 font-body">
              <Link2 size={12} /> Linked templates:
              {matrix.linked_templates.map((t) => <Badge key={t.id} variant="muted" className="text-[10px]">{t.name}</Badge>)}
            </div>
          )}
          {matrix.linked_templates.length === 0 && (
            <p className="mt-2 text-xs text-amber-700 font-body flex items-center gap-1"><Info size={12} /> Not linked yet — open a survey template and pick this matrix in the "Rating matrix" dropdown to use it.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="blue">v{matrix.version_number} · jb_v1</Badge>
          {dirty && <Badge variant="warning"><AlertTriangle size={10} /> Unsaved changes</Badge>}
        </div>
      </div>

      {!weightOk && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="jb-weight-warning">
          <AlertTriangle size={16} /> Category weights sum to {weightSum.toFixed(2)} — must equal 1.00 before you can publish.
        </div>
      )}

      <Tabs defaultValue="weights">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="weights" data-testid="jb-tab-weights">Category weights</TabsTrigger>
          <TabsTrigger value="factors" data-testid="jb-tab-factors">Factors</TabsTrigger>
          <TabsTrigger value="lookups" data-testid="jb-tab-lookups">Scoring lookups</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="jb-tab-mapping">Survey mapping</TabsTrigger>
          <TabsTrigger value="bands" data-testid="jb-tab-bands">Risk bands</TabsTrigger>
          <TabsTrigger value="attention" data-testid="jb-tab-attention">Attention rules</TabsTrigger>
          <TabsTrigger value="calculator" data-testid="jb-tab-calculator">Calculator</TabsTrigger>
          <TabsTrigger value="history" data-testid="jb-tab-history">Version history</TabsTrigger>
        </TabsList>

        {/* Category weights */}
        <TabsContent value="weights" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Category weights</CardTitle><CardDescription>Each category's share of the overall score. Must sum to 1.00.</CardDescription></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {config.categories.map((c, i) => (
                <div key={c.category_id} className="flex items-center gap-2">
                  <div className="w-44 text-sm font-body text-raptor-navy/80 truncate" title={c.rationale}>{catName(c)}</div>
                  <Input type="number" step="0.01" min="0" max="1" value={c.weight} className="h-8 w-24 font-tabular" onChange={(e) => setCat(i, Number(e.target.value))} data-testid={`jb-weight-${c.category_id}`} />
                  <span className="text-xs text-raptor-navy/40">{Math.round((c.weight || 0) * 100)}%</span>
                </div>
              ))}
              <div className="md:col-span-2 text-sm text-raptor-navy/70">Weight sum: <b className={weightOk ? 'text-emerald-600' : 'text-amber-600'}>{weightSum.toFixed(2)}</b></div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Factors */}
        <TabsContent value="factors" className="mt-4 space-y-3">
          {config.categories.map((c) => (
            <Card key={c.category_id}>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">{catName(c)}<span className="text-xs font-body text-raptor-navy/50">{(factorsByCat[c.category_id] || []).length} factors</span></CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr>
                    <th className="py-2 px-3 text-left">Factor</th><th className="py-2 px-3 text-left">Ref</th>
                    <th className="py-2 px-3 text-right">Weight in cat.</th><th className="py-2 px-3 text-right">Max pts</th>
                  </tr></thead>
                  <tbody>
                    {(factorsByCat[c.category_id] || []).map((f) => (
                      <tr key={f.factor_id} className="border-t border-border/60">
                        <td className="py-1.5 px-3">{f.survey_factor}</td>
                        <td className="py-1.5 px-3 text-xs text-raptor-navy/50 font-tabular">{f.survey_ref}</td>
                        <td className="py-1.5 px-3 text-right"><Input type="number" step="0.01" min="0" max="1" value={f.weight_in_category} className="h-7 w-20 text-xs text-right font-tabular ml-auto" onChange={(e) => setFactor(f.factor_id, 'weight_in_category', Number(e.target.value))} data-testid={`jb-fw-${f.factor_id}`} /></td>
                        <td className="py-1.5 px-3 text-right"><Input type="number" step="1" min="0" value={f.max_points} className="h-7 w-20 text-xs text-right font-tabular ml-auto" onChange={(e) => setFactor(f.factor_id, 'max_points', Number(e.target.value))} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Scoring lookups */}
        <TabsContent value="lookups" className="mt-4 space-y-3" data-testid="jb-lookups-content">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calculator size={16} /> Scoring lookups</CardTitle><CardDescription>The answer → risk-points table that drives the score. Edit the risk points (0–100) for each possible answer, then publish a new version. {(config.scoring_lookup || []).length} rows across {config.factors.length} factors.</CardDescription></CardHeader>
          </Card>
          {config.categories.map((c) => (
            <Card key={c.category_id}>
              <CardHeader className="pb-2"><CardTitle className="text-base">{catName(c)}</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {(factorsByCat[c.category_id] || []).map((f) => (
                  <div key={f.factor_id} className="border-t border-border/60 first:border-t-0">
                    <div className="px-3 py-2 bg-raptor-neutral/30 text-xs font-headline text-raptor-navy/80 flex items-center gap-2">
                      <span className="font-tabular text-raptor-navy/50">{f.survey_ref}</span> {f.survey_factor}
                      <span className="text-raptor-navy/40">· {f.scoring_type}</span>
                    </div>
                    <table className="w-full text-sm font-body">
                      <thead className="bg-raptor-neutral/20 text-[11px] uppercase text-raptor-navy/60"><tr>
                        <th className="py-1.5 px-3 text-left">Possible answer</th>
                        <th className="py-1.5 px-3 text-right">Band from</th>
                        <th className="py-1.5 px-3 text-right">Risk points</th>
                        <th className="py-1.5 px-3 text-left">Rationale</th>
                      </tr></thead>
                      <tbody>
                        {(lookupRowsByFactor[f.factor_id] || []).map(({ l, i }) => (
                          <tr key={i} className="border-t border-border/40" data-testid={`jb-lookup-${f.factor_id}-${i}`}>
                            <td className="py-1.5 px-3"><Input value={String(l.possible_answer ?? '')} className="h-7 text-xs" onChange={(e) => setLookup(i, 'possible_answer', e.target.value)} /></td>
                            <td className="py-1.5 px-3 text-right"><Input type="number" value={l.band_from == null ? '' : Number(l.band_from)} className="h-7 w-28 text-xs text-right font-tabular ml-auto" onChange={(e) => setLookup(i, 'band_from', Number(e.target.value))} /></td>
                            <td className="py-1.5 px-3 text-right">
                              <div className="flex items-center gap-2 justify-end">
                                <span className={`inline-block w-2.5 h-2.5 rounded-full ${scoreColor(l.risk_points == null ? null : Number(l.risk_points))}`} />
                                <Input type="number" step="1" min="0" max="100" value={l.risk_points == null ? '' : Number(l.risk_points)} className="h-7 w-20 text-xs text-right font-tabular" onChange={(e) => setLookup(i, 'risk_points', Number(e.target.value))} data-testid={`jb-lookup-points-${f.factor_id}-${i}`} />
                              </div>
                            </td>
                            <td className="py-1.5 px-3 text-xs text-raptor-navy/60 max-w-[260px]"><Input value={String((l as { rationale?: string }).rationale ?? '')} className="h-7 text-xs" onChange={(e) => setLookup(i, 'rationale', e.target.value)} /></td>
                          </tr>
                        ))}
                        {(lookupRowsByFactor[f.factor_id] || []).length === 0 && (
                          <tr className="border-t border-border/40"><td colSpan={4} className="py-2 px-3 text-xs text-raptor-navy/40 italic">No scoring lookup rows — this factor scores directly on its value.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Survey mapping */}
        <TabsContent value="mapping" className="mt-4 space-y-3" data-testid="jb-mapping-content">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Link2 size={16} /> Survey mapping</CardTitle>
              <CardDescription>
                Bind each factor to the survey question that feeds it. Without this, answers never reach the engine and every site scores the same (worst-case). <b>{mappedCount}</b> of <b>{config.factors.length}</b> factors mapped.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {mapLoading && <div className="text-sm text-raptor-navy/50">Loading survey questions…</div>}
              {!mapLoading && mapData && (mapData.linked_templates || []).length === 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="jb-mapping-unlinked">
                  <Unlink size={16} /> This matrix isn't linked to any survey template yet. Open a survey template and pick this matrix in the "Rating matrix" dropdown, then come back to map its questions.
                </div>
              )}
              {!mapLoading && mapData && (mapData.linked_templates || []).length > 0 && (
                <>
                  {mapData.resolution && (
                    <div className={`rounded-md border p-3 text-sm ${mapData.resolution.scored_ok === 0 ? 'border-red-300 bg-red-50 text-red-800' : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`} data-testid="jb-mapping-resolution">
                      {mapData.resolution.scored_ok === 0 ? (
                        <span><b>No answers are resolving</b> on the latest survey — every factor falls to worst-case (score {mapData.resolution.sample_score}). This usually means the <b>wrong matrix is allocated to this template</b>, or answer values don't match the scoring lookups. Check the template's Rating matrix.</span>
                      ) : (
                        <span>On the latest survey: <b>{mapData.resolution.scored_ok}</b> factors scored from real answers, {mapData.resolution.missing} missing, {mapData.resolution.unresolved} unresolved (answer didn't match a lookup row) → score <b>{mapData.resolution.sample_score}</b>. Unresolved/missing count as worst-case, so resolve those for full accuracy.</span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    {mapData.linked_templates.length > 1 && (
                      <select
                        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={mapTemplateId}
                        onChange={(e) => { setMapTemplateId(e.target.value); void loadMapping(e.target.value); }}
                        data-testid="jb-mapping-template-select"
                      >
                        {mapData.linked_templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    )}
                    <Button variant="gradient" onClick={autoMapAll} data-testid="jb-mapping-automap">
                      <Wand2 size={14} className="mr-1" /> Auto-map by name &amp; number
                    </Button>
                    <span className="text-xs text-raptor-navy/50">Then review and <b>Publish</b> to apply.</span>
                  </div>
                  {config.categories.map((c) => (
                    <Card key={c.category_id}>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">{catName(c)}</CardTitle></CardHeader>
                      <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-sm font-body">
                          <thead className="bg-raptor-neutral/40 text-[11px] uppercase text-raptor-navy/70"><tr>
                            <th className="py-1.5 px-3 text-left">Factor</th>
                            <th className="py-1.5 px-3 text-left">Ref</th>
                            <th className="py-1.5 px-3 text-left">Survey question</th>
                          </tr></thead>
                          <tbody>
                            {(factorsByCat[c.category_id] || []).map((f) => {
                              const sugg = suggestionFor(f.factor_id);
                              const mapped = !!f.question_code;
                              return (
                                <tr key={f.factor_id} className="border-t border-border/60" data-testid={`jb-map-${f.factor_id}`}>
                                  <td className="py-1.5 px-3">{f.survey_factor}</td>
                                  <td className="py-1.5 px-3 text-xs text-raptor-navy/50 font-tabular">{f.survey_ref}</td>
                                  <td className="py-1.5 px-3">
                                    <div className="flex items-center gap-2">
                                      <select
                                        className={`h-8 rounded-md border px-2 text-xs min-w-[260px] ${mapped ? 'border-emerald-300' : 'border-amber-300'}`}
                                        value={f.question_code || ''}
                                        onChange={(e) => setFactorQuestion(f.factor_id, e.target.value)}
                                        data-testid={`jb-map-select-${f.factor_id}`}
                                      >
                                        <option value="">— not mapped (scores as missing) —</option>
                                        {(mapData.questions || []).map((q) => (
                                          <option key={q.question_code} value={q.question_code}>
                                            {q.display_number ? `${q.display_number} · ` : ''}{q.text || q.question_code}
                                          </option>
                                        ))}
                                      </select>
                                      {!mapped && sugg && (
                                        <button
                                          className="text-[11px] text-raptor-blue hover:underline whitespace-nowrap"
                                          onClick={() => setFactorQuestion(f.factor_id, sugg)}
                                          data-testid={`jb-map-apply-${f.factor_id}`}
                                        >use suggestion</button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk bands */}
        <TabsContent value="bands" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Risk bands</CardTitle><CardDescription>Score ranges (0–100) and the underwriting stance for each band.</CardDescription></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm font-body"><thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr>
                <th className="py-2 px-3 text-left">Band</th><th className="py-2 px-3 text-right">From</th><th className="py-2 px-3 text-right">Up to</th><th className="py-2 px-3 text-left">Stance</th>
              </tr></thead>
                <tbody>{config.risk_bands.map((b, i) => (
                  <tr key={b.band} className="border-t border-border/60">
                    <td className="py-1.5 px-3"><span className={`inline-block px-2 py-0.5 rounded text-white text-xs ${bandColor(b.band)}`}>{b.band}</span></td>
                    <td className="py-1.5 px-3 text-right"><Input type="number" value={b.lower} className="h-7 w-20 text-xs text-right font-tabular ml-auto" onChange={(e) => setBand(i, 'lower', Number(e.target.value))} /></td>
                    <td className="py-1.5 px-3 text-right"><Input type="number" value={b.upper} className="h-7 w-20 text-xs text-right font-tabular ml-auto" onChange={(e) => setBand(i, 'upper', Number(e.target.value))} data-testid={`jb-band-upper-${i}`} /></td>
                    <td className="py-1.5 px-3"><Input value={b.stance || ''} className="h-7 text-xs" onChange={(e) => setBand(i, 'stance', e.target.value)} /></td>
                  </tr>
                ))}</tbody></table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attention rules */}
        <TabsContent value="attention" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Underwriting attention rules ({config.attention_rules.length})</CardTitle><CardDescription>Fire a flag when metric(s) cross a threshold. Toggle active and tune thresholds/level.</CardDescription></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm font-body"><thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr>
                <th className="py-2 px-3 text-left">On</th><th className="py-2 px-3 text-left">Rule</th><th className="py-2 px-3 text-left">Metric 1</th><th className="py-2 px-3 text-right">≥</th><th className="py-2 px-3 text-left">Metric 2</th><th className="py-2 px-3 text-right">≥</th><th className="py-2 px-3 text-left">Level</th>
              </tr></thead>
                <tbody>{config.attention_rules.map((r, i) => {
                  const active = String(r.active).toLowerCase() === 'yes' || String(r.active).toLowerCase() === 'true';
                  return (
                    <tr key={r.rule_id} className="border-t border-border/60" data-testid={`jb-rule-${r.rule_id}`}>
                      <td className="py-1.5 px-3"><input type="checkbox" checked={active} onChange={(e) => setRule(i, 'active', e.target.checked ? 'Yes' : 'No')} data-testid={`jb-rule-active-${r.rule_id}`} /></td>
                      <td className="py-1.5 px-3 text-xs max-w-[220px]">{r.description}</td>
                      <td className="py-1.5 px-3 text-xs text-raptor-navy/60 font-tabular">{r.metric_1}</td>
                      <td className="py-1.5 px-3 text-right"><Input type="number" value={r.threshold_1 ?? ''} className="h-7 w-24 text-xs text-right font-tabular ml-auto" onChange={(e) => setRule(i, 'threshold_1', Number(e.target.value))} /></td>
                      <td className="py-1.5 px-3 text-xs text-raptor-navy/60 font-tabular">{r.metric_2 || '—'}</td>
                      <td className="py-1.5 px-3 text-right">{r.metric_2 ? <Input type="number" value={r.threshold_2 ?? ''} className="h-7 w-24 text-xs text-right font-tabular ml-auto" onChange={(e) => setRule(i, 'threshold_2', Number(e.target.value))} /> : '—'}</td>
                      <td className="py-1.5 px-3"><Badge variant={attnVariant(r.attention_level)}>{r.attention_level}</Badge></td>
                    </tr>
                  );
                })}</tbody></table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calculator */}
        <TabsContent value="calculator" className="mt-4 space-y-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calculator size={16} /> Try the rating</CardTitle><CardDescription>Answer the factors to preview a score. The calculator uses the last <b>published</b> version — publish your edits first to try them.</CardDescription></CardHeader></Card>
          {config.categories.map((c) => (
            <Card key={c.category_id}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{catName(c)}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(factorsByCat[c.category_id] || []).map((f) => (
                  <div key={f.factor_id} className="grid md:grid-cols-2 gap-2 items-center">
                    <Label className="text-sm font-body text-raptor-navy/80" title={f.survey_factor}>{f.survey_ref} · {f.survey_factor}</Label>
                    {isDropdown(f) ? (
                      <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={answers[f.factor_id] ?? ''} onChange={(e) => setAnswers({ ...answers, [f.factor_id]: e.target.value })} data-testid={`jb-calc-${f.factor_id}`}>
                        <option value="">— Not answered —</option>
                        {(lookupByFactor[f.factor_id] || []).map((l, j) => <option key={j} value={String(l.possible_answer ?? '')}>{String(l.possible_answer ?? '')}</option>)}
                      </select>
                    ) : (
                      <Input type="text" placeholder="value or answer" value={answers[f.factor_id] ?? ''} onChange={(e) => setAnswers({ ...answers, [f.factor_id]: e.target.value })} data-testid={`jb-calc-${f.factor_id}`} />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
          <div className="flex justify-end"><Button variant="gradient" onClick={() => void runCalc()} disabled={calculating} data-testid="jb-calc-run"><Calculator size={16} /> {calculating ? 'Scoring…' : 'Compute rating'}</Button></div>
          {calc && (
            <div className="space-y-4" data-testid="jb-calc-result">
              <div className="grid md:grid-cols-4 gap-4">
                <Card><CardContent className="p-5 text-center">
                  <div className="text-xs uppercase text-raptor-navy/50 font-headline tracking-wide">Overall score</div>
                  <div className="text-5xl font-tabular text-raptor-navy mt-1" data-testid="jb-calc-score">{String(calc.overall_score)}</div>
                  <div className={`inline-block mt-2 px-3 py-1 rounded-full text-white text-sm ${bandColor(calc.risk_band as string)}`} data-testid="jb-calc-band">{String(calc.risk_band)}</div>
                </CardContent></Card>
                <Card className="md:col-span-3"><CardContent className="p-5">
                  <div className="text-xs uppercase text-raptor-navy/50 font-headline tracking-wide">Underwriting attention</div>
                  <Badge variant={attnVariant(calc.attention_level as string)} className="mt-1 text-sm">{String(calc.attention_level)}</Badge>
                  <div className="text-xs text-raptor-navy/50 font-body mt-1">{String(calc.risk_band_stance ?? '')}</div>
                  {Array.isArray(calc.triggered_rules) && (calc.triggered_rules as unknown[]).length > 0 && (
                    <div className="mt-3 space-y-1">{(calc.triggered_rules as Array<{ rule_id: string; level: string; description: string }>).map((t) => (
                      <div key={t.rule_id} className="flex items-center gap-2 text-sm font-body text-raptor-navy/80"><AlertTriangle size={13} className="text-orange-500" /> <b>{t.level}</b> · {t.description}</div>
                    ))}</div>
                  )}
                </CardContent></Card>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-base">Category breakdown</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {(calc.category_breakdown as Array<{ category_id: string; name?: string; score: number | null; weight: number }>).map((cb) => (
                    <div key={cb.category_id} className="flex items-center gap-3">
                      <div className="w-40 text-sm font-body text-raptor-navy/80 truncate">{catName(cb)}</div>
                      <div className="flex-1 h-3 rounded-full bg-raptor-neutral/40 overflow-hidden"><div className={`h-full ${scoreColor(cb.score)}`} style={{ width: `${cb.score ?? 0}%` }} /></div>
                      <div className="w-14 text-right font-tabular text-sm">{cb.score ?? 'N/A'}</div>
                      <div className="w-12 text-right text-xs text-raptor-navy/40">{Math.round((cb.weight || 0) * 100)}%</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><History size={16} /> Version history ({versions.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm font-body"><thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70"><tr><th className="py-2 px-3 text-left">Version</th><th className="py-2 px-3 text-left">Summary</th><th className="py-2 px-3 text-left">Author</th><th className="py-2 px-3 text-left">Created</th><th className="py-2 px-3"></th></tr></thead>
                <tbody>{versions.map((v) => (
                  <tr key={v.id} className="border-t border-border/60" data-testid={`jb-version-${v.version_number}`}>
                    <td className="py-2 px-3 font-tabular font-semibold">v{v.version_number}</td>
                    <td className="py-2 px-3 text-xs">{v.change_summary || <span className="text-raptor-navy/40 italic">no summary</span>}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/60">{v.created_by_email || '—'}</td>
                    <td className="py-2 px-3 text-xs text-raptor-navy/60">{v.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 px-3">{v.is_current && <Badge variant="success"><ShieldCheck size={10} /> Current</Badge>}</td>
                  </tr>
                ))}</tbody></table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Publish bar */}
      <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-border/60 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-3 flex-wrap" data-testid="jb-publish-bar">
        <Info size={14} className="text-raptor-navy/60" />
        <div className="text-xs text-raptor-navy/70 flex-1 min-w-[200px]">Publishing creates <b>v{matrix.version_number + 1}</b>. Existing assessments keep their frozen version.</div>
        <Input placeholder="What changed? (e.g. 'Raised alarm weight')" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} className="h-8 text-xs w-72" data-testid="jb-change-summary" />
        <Button variant="gradient" onClick={() => void publish()} disabled={!dirty || saving || !weightOk} data-testid="jb-publish-btn"><Save size={14} className="mr-1" /> {saving ? 'Publishing…' : `Publish v${matrix.version_number + 1}`}</Button>
      </div>
    </div>
  );
};

export default JbMatrixEditor;
